import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useToast } from "../lib/use-toast";
import { useCompareEntities, useImportEntities, useProcessChecklists, useChecklistDependencyImport, useChecklistImport, useAuthenticate, useProcessMigrationImport, ChecklistValidationError, ChecklistValidationReport } from "../lib/api";
import { useInstanceFacilities, useLinkedChecklistScan, type LinkedChecklistScanEntry } from "../lib/api-central-mu";
import ChecklistScanGroups from "../components/workflow/ChecklistScanGroups";
import LinkedResolutionFlow, { type LinkedStage } from "../components/workflow/linked/LinkedResolutionFlow";
import LinkedPipelinePanel from "../components/workflow/linked/LinkedPipelinePanel";
import ProcessStepper from "../components/workflow/ProcessStepper";
import { useWorkflowStore } from "../store/useWorkflowStore";
import type { ImportedChecklistItem, OntologyRecord } from "../store/useWorkflowStore";
import { useConnectedServices } from "../lib/api";
import { useAuthStore } from "../store/useAuthStore";

import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import WorkflowStates from "../components/workflow/WorkflowStates";
import MigrationOutcomePanel, { type MigrationOutcomeSummary } from "../components/workflow/MigrationOutcomePanel";
import WorkflowDetailsForm from "../components/workflow/WorkflowDetailsForm";
import WorkflowSourceForm from "../components/workflow/WorkflowSourceForm";
import WorkflowResults from "../components/workflow/WorkflowResults";
import TargetInstanceForm from "../components/workflow/TargetInstanceForm";
import { InstancePicker } from "../components/InstancePicker";
import { useInstancePairStore } from "../store/useInstancePairStore";
import { Sparkles, Workflow } from "lucide-react";

/**
 * WorkflowDetailPage component
 * 
 * Main page for workflow details and migration process
 */
const WorkflowDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // Extract the workflow type from the URL path
  const workflowType = window.location.pathname.includes('/workflows/process/') ? 'process' : 'ontology';
  const { toast } = useToast();
  const { isAuthenticated, isDefaultUser } = useAuthStore();
  const [redirecting, setRedirecting] = useState(false);

  // Block DEFAULT_USERs from accessing process migration via direct URL
  useEffect(() => {
    if (isDefaultUser && workflowType === 'process') {
      navigate('/workflows', { replace: true });
    }
  }, [isDefaultUser, workflowType, navigate]);

  // Workflow store
  const {
    viewState,
    formData,
    currentWorkflow,
    setViewState,
    processChecklistResults,
    resetForm,
    getSelectedItems,
    selectedClient,
    selectedChecklists,
    selectedFacilityId,
    selectedUseCaseId,
    targetFacilityId,
    targetUseCaseId,
    targetAuthorId,
    targetChecklistId,
    linkedScan,
    plainImportChecklistIds,
  } = useWorkflowStore();

  // Local state for loading
  const [isLoadingWorkflow] = useState(false);

  // Inner stage of the linked-resolution flow — drives the "Resolve links / Review / Done" part
  // of the process stepper while viewState === "linked".
  const [linkedStage, setLinkedStage] = useState<LinkedStage>("resolve");

  // Per-checklist reports from an aborted multi-checklist migration (server-side validation).
  // Rendered by WorkflowResults so each checklist shows its OWN missing items.
  const [validationReports, setValidationReports] = useState<ChecklistValidationReport[] | null>(null);

  const [migrationOutcome, setMigrationOutcome] = useState<MigrationOutcomeSummary | null>(null);

  // Source service id (from InstanceSelector via the workflow store). When set, we're on the
  // central-MU path — facilities come from MU's /api/instances/{id}/facilities instead of a
  // direct browser → streem-backend /v1/auth/login.
  const { selectedServiceId } = useWorkflowStore();
  const storeTargetServiceId = useWorkflowStore((s) => s.targetServiceId);

  // Bridge the shared source⇄target instance picker (useInstancePairStore) into the workflow
  // store, so the existing source/target selectors (which read selectedServiceId / targetServiceId
  // + the host / targetHost form fields) drive off the single InstancePicker bar instead of
  // separate per-step instance dropdowns.
  const setSelectedServiceId = useWorkflowStore((s) => s.setSelectedServiceId);
  const setTargetServiceId = useWorkflowStore((s) => s.setTargetServiceId);
  const updateFormField = useWorkflowStore((s) => s.updateFormField);
  const pairSourceId = useInstancePairStore((s) => s.sourceServiceId);
  const pairTargetId = useInstancePairStore((s) => s.targetServiceId);

  // Prev-value guard: the clear below must only fire on an ACTUAL source change, not on mount
  // (the pair is session-persisted; wiping on mount would lose the persisted facility selection).
  const prevPairSourceIdRef = useRef(pairSourceId);
  useEffect(() => {
    // Re-seed whenever the store drifts out of sync with the pair — not just on pair change.
    // resetForm()/setCurrentWorkflow() null selectedServiceId while this page stays mounted
    // (sidebar "start migration" of the same type, wizard Back button); without this the
    // facility selector stays disabled until a full page refresh remounts us.
    const nextSourceId = pairSourceId != null ? String(pairSourceId) : null;
    if (selectedServiceId !== nextSourceId) {
      setSelectedServiceId(nextSourceId);
      updateFormField("host", nextSourceId ?? "");
    }
    if (prevPairSourceIdRef.current !== pairSourceId) {
      prevPairSourceIdRef.current = pairSourceId;
      // Source instance changed: everything picked against the old source is stale/foreign —
      // clear it so the final import can never run with another instance's facility/checklists.
      const store = useWorkflowStore.getState();
      store.setSelectedFacilityId(null);
      store.setSelectedUseCaseId(null);
      store.setSelectedUseCaseName(null);
      store.clearSelectedChecklists();
      store.setLinkedScan(null);
      store.setPlainImportChecklistIds(null);
    }
  }, [pairSourceId, selectedServiceId, setSelectedServiceId, updateFormField]);

  useEffect(() => {
    // Same drift-repair as the source effect above: resetForm() nulls targetServiceId in place.
    const nextTargetId = pairTargetId != null ? String(pairTargetId) : null;
    if (storeTargetServiceId !== nextTargetId) {
      setTargetServiceId(nextTargetId);
      updateFormField("targetHost", nextTargetId ?? "");
    }
  }, [pairTargetId, storeTargetServiceId, setTargetServiceId, updateFormField]);
  const sourceServiceIdNumber = selectedServiceId ? Number(selectedServiceId) : null;
  const useCentralMU = !!sourceServiceIdNumber;

  // Split routing (derived, never mutates selectedChecklists): checklists flagged by the scan as
  // configuring a linked process go through the linked-resolution flow; the rest go through the
  // plain import. linkedScan == null ⇒ scan unavailable → legacy whole-selection routing.
  const linkedIdSet = new Set((linkedScan ?? []).filter((e) => e.hasLinkedProcess).map((e) => e.checklistId));
  const linkedSubset = selectedChecklists.filter((c) => linkedIdSet.has(c.id));
  const standardSubset = selectedChecklists.filter((c) => !linkedIdSet.has(c.id));
  // This run includes (or included) a linked-resolution step — keep the 5-step stepper after it.
  const hadLinkedStep = linkedScan != null && linkedSubset.length > 0;
  // Mixed run where the plain confirm covered only the standard subset: tell the user the linked
  // group's migrations are unaffected by a validation abort.
  const abortContextNote =
    plainImportChecklistIds && plainImportChecklistIds.length < selectedChecklists.length && linkedSubset.length > 0
      ? "Only the standard checklists were aborted — linked-process checklists migrated in the previous step are unaffected."
      : undefined;

  // Legacy authenticate hook — kept for non-central-MU code paths, gated so it never fires
  // when central-MU is active (otherwise it would POST the legacy {clientId, clientSecret,
  // facilityId} shape to streem-backend and 4xx).
  const { data: authData } = useAuthenticate(useCentralMU ? null : selectedClient);

  // Central-MU equivalent — fetches facilities via MU for the chosen source instance.
  const { data: centralMUFacilities = [] } = useInstanceFacilities(
    useCentralMU ? sourceServiceIdNumber : null,
  );

  // Unified facility list for facility-name lookups inside compare/import handlers below.
  const facilitiesForLookup = useCentralMU
    ? (centralMUFacilities as unknown as Array<{ id: string; name: string }>)
    : (authData?.facilities || []);

  // Fetch connected services
  const { refetch } = useConnectedServices();

  // Get the API hooks
  const { mutate: compareEntities } = useCompareEntities();
  const { mutate: importEntities } = useImportEntities();
  const { mutate: processChecklists } = useProcessChecklists();
  // Ontology migration uses checklist-dependency import, Process migration uses checklists import
  const { fetchDependencies, isLoading: isImportingDependencies } = useChecklistDependencyImport();
  const { importChecklists, isLoading: isImportingChecklists } = useChecklistImport();
  // Process migration final import
  const { processImport, isLoading: isProcessingImport } = useProcessMigrationImport();
  // Linked-process scan when leaving the selection step (central-MU only).
  const linkedScanMutation = useLinkedChecklistScan();

  // Refetch connected services when authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      refetch();
    }
  }, [isAuthenticated, refetch]);

  // Check if we have the workflow in the store
  useEffect(() => {
    // If we have an ID but no current workflow, check if we need to redirect
    if (id && !currentWorkflow && !redirecting) {
      setRedirecting(true);
      toast({
        variant: "destructive",
        title: "Workflow Not Found",
        description: "The requested workflow could not be found.",
      });
      
      const timer = setTimeout(() => {
        navigate("/workflow");
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [id, currentWorkflow, navigate, toast, redirecting]);
  
  // Determine if we should show error state
  const shouldShowError = !currentWorkflow && !isLoadingWorkflow;

  // Handle source form completion
  const handleSourceFormComplete = async () => {
    const {
      selectedChecklists,
      selectedFacilityId,
      selectedServiceId
    } = useWorkflowStore.getState();

    if (selectedChecklists.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No checklists selected",
      });
      return;
    }

    if (!selectedFacilityId || !selectedServiceId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Facility and Client must be selected",
      });
      return;
    }

    // If we're in process migration, call /checklist-dependency to get ontologyRecords
    if (workflowType === 'process') {
      try {
        setViewState("loading");

        // Extract just the IDs from the selected checklists
        const checklistIds = selectedChecklists.map(checklist => checklist.id);

        // Prepare the API request parameters (without target params)
        const importParams = {
          serviceId: selectedServiceId.toString(),
          facilityId: selectedFacilityId,
          checklistIds: checklistIds,
          databaseName: formData["databaseName"] || "",
          hostId: formData["host"] || ""
        };

        console.log('Calling /checklist-dependency/import API for process migration with params:', importParams);

        // Linked-process scan in parallel with the dependency fetch (central-MU only). Outcomes:
        // entries → grouped scan-review step; forbidden (403) → fail closed, stay on the form;
        // null (infra failure / legacy path) → degrade to today's ungrouped routing.
        type ScanOutcome = { entries: LinkedChecklistScanEntry[] } | { forbidden: string } | null;
        const scanPromise: Promise<ScanOutcome> = useCentralMU
          ? linkedScanMutation
              .mutateAsync({
                sourceServiceId: Number(selectedServiceId),
                sourceFacilityId: selectedFacilityId,
                checklistIds,
              })
              .then(
                (r): ScanOutcome => ({ entries: r.entries }),
                (err): ScanOutcome => {
                  const status = (err as any)?.response?.status;
                  if (status === 403) {
                    return { forbidden: (err as any)?.response?.data?.message || "You are not authorized to run process migrations on the source instance." };
                  }
                  console.error("Linked-process scan failed:", err);
                  return null;
                }
              )
          : Promise.resolve(null);

        // Call the checklist-dependency import API for process migration
        const [data, scanOutcome] = await Promise.all([fetchDependencies(importParams), scanPromise]);

        // Store ontologyRecords in workflow store
        if (data && typeof data === 'object' && 'ontologyRecords' in data) {
          useWorkflowStore.getState().processOntologyRecords(data as { ontologyRecords: OntologyRecord[]; autoIdObjectTypeIds?: string[] });
        }

        if (scanOutcome && 'forbidden' in scanOutcome) {
          // Authorization denial is fail-closed — don't silently degrade into a flow that
          // /migrate would deny later anyway.
          toast({ variant: "destructive", title: "Not authorized", description: scanOutcome.forbidden });
          setViewState("form");
          return;
        }

        const scanEntries = scanOutcome ? scanOutcome.entries : null;
        useWorkflowStore.getState().setLinkedScan(scanEntries);
        if (scanEntries) {
          setViewState("scan-review");
        } else {
          if (useCentralMU) {
            toast({
              title: "Could not detect linked processes",
              description: "Continuing without grouping — linked checklists are still resolved after the target step.",
            });
          }
          setViewState("target-selection");
        }
      } catch (error) {
        console.error("Error calling /checklist-dependency for process migration:", error);

        toast({
          variant: "destructive",
          title: "Import Failed",
          description:
            error instanceof Error ? error.message : "Failed to fetch checklist dependencies",
        });

        setViewState("form");
      }
    } else {
      // Ontology migration: the target instance is chosen in the source⇄target bar above.
      // Metadata-only ontology is facility-agnostic, so we run extraction directly. But when
      // "include objects" is on, EntityObject instances are facility-scoped — route through the
      // target step so the user can pick the target facility first.
      const { targetServiceId, includeObjects } = useWorkflowStore.getState();
      if (!targetServiceId) {
        toast({
          variant: "destructive",
          title: "Select a target instance",
          description: "Pick the target in the source → target bar above before continuing.",
        });
        return;
      }
      if (includeObjects) {
        setViewState("target-selection");
      } else {
        handleProcessImport();
      }
    }
  };

  // Handle target form completion
  const handleTargetFormComplete = async () => {
    if (workflowType === 'process') {
      // Split routing: with a scan result, only the linked subset enters the linked-resolution
      // step (the standard subset follows via its done-stage handoff); an all-standard selection
      // skips the linked flow entirely. Without a scan (failure / legacy), the WHOLE selection
      // goes through the linked step, which falls back to the plain review when nothing is linked
      // — exactly the pre-scan behavior.
      const { targetServiceId, targetFacilityId, linkedScan: scan } = useWorkflowStore.getState();
      if (useCentralMU && targetServiceId && targetFacilityId) {
        if (scan == null || linkedSubset.length > 0) {
          setLinkedStage("resolve");
          setViewState("linked");
          return;
        }
        // All-standard: no linked flow, straight to the plain review.
      }
      await continuePlainProcessReview(selectedChecklists);
    } else {
      // Ontology migration: target instance is picked, NOW run the orchestrator extraction.
      // Records preview will then have a real targetServiceId in store for the Push step.
      handleProcessImport();
    }
  };

  // Plain (non-linked) process review: re-run the ontology dependency check WITH the chosen target
  // so the records' sync status reflects the target (e.g. an ObjectType present under a different id
  // shows CONFLICT, not the default MISSING from the no-target source-form call). Then show results.
  // Takes the checklists it should cover (the standard subset in a mixed run, or the whole
  // selection) and records that subset for the confirm step.
  const continuePlainProcessReview = async (checklists: typeof selectedChecklists) => {
    try {
      setViewState("loading");
      const {
        selectedFacilityId, selectedServiceId, targetServiceId, targetFacilityId,
      } = useWorkflowStore.getState();
      useWorkflowStore.getState().setPlainImportChecklistIds(checklists.map(c => c.id));

      const importParams: any = {
        serviceId: selectedServiceId ? selectedServiceId.toString() : "",
        facilityId: selectedFacilityId || "",
        checklistIds: checklists.map(c => c.id),
        databaseName: formData["databaseName"] || "",
        hostId: formData["host"] || "",
        // Always surface referenced EntityObjects in the process review. The migration's own
        // validation gate ALWAYS resolves these object references and blocks on the missing ones
        // (ProcessMigrationService.validateChecklistElement), so the table must show them too —
        // otherwise the red "Missing objects on target" footer lists objects the table never
        // displays. For process migration the MU never pushes these objects (they must already
        // exist on target), so including them here is purely informational.
        includeObjects: true,
      };
      if (targetServiceId) importParams.targetServiceId = targetServiceId.toString();
      if (targetFacilityId) importParams.targetFacilityId = targetFacilityId;

      const data = await fetchDependencies(importParams);
      if (data && typeof data === 'object' && 'ontologyRecords' in data) {
        useWorkflowStore.getState().processOntologyRecords(data as { ontologyRecords: OntologyRecord[] });
      }
    } catch (error) {
      console.error("Error re-fetching dependencies with target:", error);
      toast({
        variant: "destructive",
        title: "Failed to compare against target",
        description: error instanceof Error ? error.message : "Could not refresh ontology status",
      });
    }
    setViewState("results");
  };

  // Handle process comparison (for process migration after target selection)
  const handleCompareProcess = async () => {
    try {
      setViewState("loading");

      const {
        selectedChecklists,
        selectedFacilityId,
        selectedServiceId,
        selectedUseCaseName,
        targetServiceId,
        targetFacilityId,
        targetFacilityName,
        targetAuthorId,
        targetUseCaseId,
        targetUseCaseName,
        includeObjects,
        currentWorkflow,
        ontologyRecords
      } = useWorkflowStore.getState();

      console.log(`Comparing checklists for migration - source facility: ${selectedFacilityId}, target facility: ${targetFacilityId}`);

      // Extract just the IDs from the selected checklists
      const checklistIds = selectedChecklists.map(checklist => checklist.id);

      // Get facility name from the authentication data
      // Central-MU: facilitiesForLookup is sourced from /api/instances/{id}/facilities when
      // selectedServiceId is set; otherwise from the legacy useAuthenticate response.
      const facilities = facilitiesForLookup || [];
      const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
      const facilityName = selectedFacility?.name || "";

      // Prepare the API request parameters for comparison
      const compareParams: any = {
        // Source information
        databaseName: formData["databaseName"] || "",
        host: formData["host"] || "",
        serviceId: selectedServiceId ? selectedServiceId.toString() : "",
        facilityId: selectedFacilityId || "",
        facilityName: facilityName,
        checklistIds: checklistIds,
        checklistNames: selectedChecklists.map(c => `${c.name} (${c.code})`),

        // Workflow information
        code: currentWorkflow?.id || "",
        name: currentWorkflow?.name || "",
        description: currentWorkflow?.description || "",

        // Target information
        targetServiceId: targetServiceId ? targetServiceId.toString() : "",
        targetFacilityId: targetFacilityId || "",
        targetFacilityName: targetFacilityName || "",
        targetUseCaseId: targetUseCaseId || "",
        targetDatabaseName: formData["targetDatabaseName"] || "",
        targetHost: formData["targetHost"] || "",

        // Use-case names for object-instance audit lines (Ontology + Objects).
        useCaseName: selectedUseCaseName || "",
        targetUseCaseName: targetUseCaseName || "",

        // Migration mode: whether object instances travel with the ontology ("Ontology + Objects").
        includeObjects: !!includeObjects,

        // Include import data from /import call
        importData: ontologyRecords || []
      };

      // Include target author if selected
      if (targetAuthorId) {
        compareParams.targetAuthorIds = [targetAuthorId];
      }

      console.log('Calling /compare API with params:', compareParams);

      // Call the compare API
      const data = await compareEntities(compareParams);

      // Check if the response has ontologyRecords
      if (data && typeof data === 'object' && 'ontologyRecords' in data) {
        // Process ontology records
        useWorkflowStore.getState().processOntologyRecords(data as { ontologyRecords: OntologyRecord[]; autoIdObjectTypeIds?: string[] });
      }else if (data && typeof data === 'object' && 'objectTypes' in data) {
        // MigrationResult shape: the ObjectTypes live under .objectTypes alongside the summary.
        processChecklistResults((data as any).objectTypes as ImportedChecklistItem[]);
      }  else {
        // Fallback to old format for backward compatibility
        processChecklistResults(data as ImportedChecklistItem[]);
      }

      setViewState("results");
    } catch (error) {
      console.error("Error comparing checklists:", error);

      toast({
        variant: "destructive",
        title: "Comparison Failed",
        description:
          error instanceof Error ? error.message : "Failed to compare checklists",
      });

      setViewState("target-selection");
    }
  };

  // Handle process import (for ontology migration - called after source form)
  const handleProcessImport = async () => {
    try {
      setViewState("loading");

      const {
        selectedChecklists,
        selectedFacilityId,
        selectedServiceId,
        targetServiceId,
        targetFacilityId,
        targetAuthorId,
        targetUseCaseId,
        includeObjects
      } = useWorkflowStore.getState();

      console.log(`Importing checklist dependencies for facility: ${selectedFacilityId}, serviceId: ${selectedServiceId}`);

      // Extract just the IDs from the selected checklists
      const checklistIds = selectedChecklists.map(checklist => checklist.id);

      // Prepare the API request parameters
      const importParams: any = {
        serviceId: selectedServiceId ? selectedServiceId.toString() : "",
        facilityId: selectedFacilityId || "",
        checklistIds: checklistIds,
        checklistNames: selectedChecklists.map(c => `${c.name} (${c.code})`),
        databaseName: formData["databaseName"] || "",
        hostId: formData["host"] || ""
      };

      // Add target parameters for process migration
      if (workflowType === 'process' && targetServiceId && targetFacilityId) {
        importParams.targetServiceId = targetServiceId.toString();
        importParams.targetFacilityId = targetFacilityId;

        // Include target author if selected
        if (targetAuthorId) {
          importParams.targetAuthorIds = [targetAuthorId];
        }

        // Include target use case if selected
        if (targetUseCaseId) {
          importParams.targetUseCaseId = targetUseCaseId;
        }
      } else if (targetServiceId) {
        // Ontology migration: send the target chosen in the source→target bar so ontology
        // sync-status compares against the actual target instance (not the static config default).
        importParams.targetServiceId = targetServiceId.toString();
        if (targetFacilityId) {
          importParams.targetFacilityId = targetFacilityId;
        }
      }

      // For ontology migration, opt-in to also discovering referenced EntityObjects.
      if (workflowType !== 'process' && includeObjects) {
        importParams.includeObjects = true;
      }

      // Call the appropriate API based on workflow type
      // Ontology migration → /api/checklist-dependency/import
      // Process migration → /api/checklists/import
      const data = workflowType === 'process'
        ? await importChecklists(importParams)
        : await fetchDependencies(importParams);

      // Check if the response has ontologyRecords
      if (data && typeof data === 'object' && 'ontologyRecords' in data) {
        // Process ontology records
        useWorkflowStore.getState().processOntologyRecords(data as { ontologyRecords: OntologyRecord[]; autoIdObjectTypeIds?: string[] });
      } else {
        // Fallback to old format for backward compatibility
        processChecklistResults(data as ImportedChecklistItem[]);
      }

      setViewState("results");
    } catch (error) {
      console.error("Error importing checklist dependencies:", error);

      toast({
        variant: "destructive",
        title: "Import Failed",
        description:
          error instanceof Error ? error.message : "Failed to import checklist dependencies",
      });

      setViewState("form");
    }
  };

  // Handle back button click
  const handleBack = () => {
    resetForm();
    setValidationReports(null);
    setMigrationOutcome(null);
    setViewState("form");
  };

  // Handle reset and navigate to home
  const handleReset = () => {
    resetForm();
    setValidationReports(null);
    setMigrationOutcome(null);
    navigate("/workflow");
  };

  // Handle confirm button click - calls /compare for ontology, /checklists/import for process
  const handleConfirm = async () => {
    setMigrationOutcome(null); // stale outcome from a previous run must not leak into this one
    try {
      // Get the appropriate selected data based on what we have
      const {
        ontologyRecords,
        getSelectedOntologyRecords,
        getSelectedItems,
        currentWorkflow,
        selectedFacilityId,
        selectedServiceId,
        selectedUseCaseId,
        selectedUseCaseName,
        selectedChecklists,
        targetServiceId,
        targetFacilityId,
        targetFacilityName,
        targetUseCaseId,
        targetUseCaseName,
        targetAuthorId,
        includeObjects,
        counterModeByObjectType
      } = useWorkflowStore.getState();

      // The plain confirm covers only the subset recorded by continuePlainProcessReview (the
      // standard group in a mixed linked/standard run); null ⇒ the whole selection.
      const plainIds = useWorkflowStore.getState().plainImportChecklistIds;
      const plainChecklists = plainIds
        ? selectedChecklists.filter(c => plainIds.includes(c.id))
        : selectedChecklists;
      const checklistIds = plainChecklists.map(checklist => checklist.id);

      setValidationReports(null);
      setViewState("migrating");

      if (workflowType === 'process') {
        // Process migration: Call /api/checklists/import with GET query params
        console.log('Calling /checklists/import API for process migration');

        // Central-MU: facilitiesForLookup is sourced from /api/instances/{id}/facilities when
      // selectedServiceId is set; otherwise from the legacy useAuthenticate response.
      const facilities = facilitiesForLookup || [];
        const selectedFacility = facilities.find((f: any) => f.id === selectedFacilityId);
        const facilityName = selectedFacility?.name || "";

        const result: any = await processImport({
          checklistIds: checklistIds,
          checklistNames: plainChecklists.map(c => `${c.name} (${c.code})`),
          serviceId: selectedServiceId ? selectedServiceId.toString() : "",
          facilityId: selectedFacilityId || "",
          facilityName: facilityName,
          databaseName: formData["databaseName"] || "",
          hostId: formData["host"] || "",
          targetServiceId: targetServiceId ? targetServiceId.toString() : "",
          targetFacilityId: targetFacilityId || "",
          targetFacilityName: targetFacilityName || "",
          targetAuthorIds: targetAuthorId || undefined,
          targetUseCaseId: targetUseCaseId || undefined,
          targetChecklistId: useWorkflowStore.getState().targetChecklistId || undefined,
          // Use-case names so migration audits show names, not ids.
          sourceUseCaseId: selectedUseCaseId || undefined,
          sourceUseCaseName: selectedUseCaseName || undefined,
          targetUseCaseName: targetUseCaseName || undefined,
          // Wizard description → migration-logs header ("No description provided" when empty).
          description: currentWorkflow?.description || undefined
        });

        // A 200 is not success by itself — the backend returns the parsed checklists; an empty
        // list means nothing was imported (e.g. stale selections after an instance change).
        const imported = Array.isArray(result?.checklists) ? result.checklists.length : 0;
        if (imported === 0) {
          toast({
            variant: "destructive",
            title: "Import Failed",
            description: "No checklists were imported to the target. Verify the source/target instances and selections, then retry.",
          });
          setViewState("results");
          return;
        }
        if (imported < checklistIds.length) {
          toast({
            variant: "destructive",
            title: "Migration completed with failures",
            description: `Imported ${imported} of ${checklistIds.length} checklist(s).`,
          });
        } else {
          toast({
            title: "Import Successful",
            description: `Imported ${imported} checklist(s).`,
          });
        }
      } else {
        // Ontology migration: Call /api/object-type-aggregation/compare
        const hasOntologyRecords = ontologyRecords.length > 0;

        // Use ontology records if available, otherwise use checklist items
        const selectedData = hasOntologyRecords
          ? getSelectedOntologyRecords()
          : getSelectedItems();

        if (selectedData.length === 0) {
          toast({
            variant: "destructive",
            title: "No Items Selected",
            description: "Please select at least one item to migrate",
          });
          setViewState("results");
          return;
        }

        // Object instances are facility-scoped — streem rejects creating them at the Global Portal (-1).
        // Require a real target facility when any OBJECT record is being migrated.
        const migratingObjects = selectedData.some((r: any) => r?.type === "OBJECT");
        if (migratingObjects && (!targetFacilityId || targetFacilityId === "-1")) {
          toast({
            variant: "destructive",
            title: "Target Facility Required",
            description: "Select a target facility before migrating object instances.",
          });
          setViewState("results");
          return;
        }

        // Get facility name from the authentication data
        // Central-MU: facilitiesForLookup is sourced from /api/instances/{id}/facilities when
      // selectedServiceId is set; otherwise from the legacy useAuthenticate response.
      const facilities = facilitiesForLookup || [];
        const selectedFacility = facilities.find(f => f.id === selectedFacilityId);
        const facilityName = selectedFacility?.name || "";

        // Create the import data object for ontology migration
        const importData: any = {
          // Source information
          databaseName: formData["databaseName"] || "",
          host: formData["host"] || "",
          serviceId: selectedServiceId ? selectedServiceId.toString() : "",
          facilityId: selectedFacilityId || "",
          facilityName: facilityName,
          checklistIds: checklistIds,
          checklistNames: selectedChecklists.map(c => `${c.name} (${c.code})`),

          // Workflow information
          code: currentWorkflow?.id || "",
          name: currentWorkflow?.name || "",
          description: currentWorkflow?.description || "",

          // Selected items to import
          importData: selectedData,

          // Target information
          targetServiceId: targetServiceId ? targetServiceId.toString() : "",
          targetFacilityId: targetFacilityId || "",
          targetFacilityName: targetFacilityName || "",
          targetUseCaseId: targetUseCaseId || "",
          targetDatabaseName: formData["targetDatabaseName"] || "",
          targetHost: formData["targetHost"] || "",

          // Use-case names for object-instance audit lines (Ontology + Objects).
          useCaseName: selectedUseCaseName || "",
          targetUseCaseName: targetUseCaseName || "",

          // Migration mode: whether object instances travel with the ontology ("Ontology + Objects").
          includeObjects: !!includeObjects,

          // Per-ObjectType Auto-ID counter choice (New/reset vs Previous). Migrated inside /compare.
          counterModeByObjectType: counterModeByObjectType || {}
        };

        // Include target author if selected
        if (targetAuthorId) {
          importData.targetAuthorIds = [targetAuthorId];
        }

        console.log('Calling /compare API for ontology migration:', {
          ...importData,
          importData: `[${selectedData.length} items]` // Log count instead of full array
        });

        const result: any = await compareEntities(importData);
        // Surface the per-record migration summary (processed/skipped/failed + reasons) rather than a
        // blanket success. A non-zero failed count is reported as a destructive toast, and the full
        // per-record outcome list is kept for the final screen (MigrationOutcomePanel).
        if (result && typeof result === 'object' && 'message' in result) {
          const failed = Number(result.failed ?? 0);
          setMigrationOutcome(result as MigrationOutcomeSummary);
          toast({
            variant: failed > 0 ? "destructive" : undefined,
            title: failed > 0 ? "Migration completed with failures" : "Migration completed",
            description: String(result.message),
          });
        } else {
          toast({
            title: "Import Successful",
            description: "All selected entities have been imported successfully",
          });
        }
      }

      setViewState("success");
    } catch (error) {
      console.error("Error importing entities:", error);

      if (error instanceof ChecklistValidationError) {
        // Batch aborted by server-side per-checklist validation: show each checklist's OWN
        // missing items in the results view instead of one flat error string.
        setValidationReports(error.reports);
        toast({
          variant: "destructive",
          title: "Migration aborted — nothing was migrated",
          description: "One or more checklists reference items missing on the target. See the per-checklist report below.",
        });
        setViewState("results");
        return;
      }

      toast({
        variant: "destructive",
        title: "Import Failed",
        description:
          error instanceof Error ? error.message : "Failed to import entities",
      });

      setViewState("results");
    }
  };

  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "3rem",
      } as React.CSSProperties}
      className="min-h-screen"
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="pt-0">
        <SiteHeader />
        <div className="flex flex-1 flex-col p-4">
          {isLoadingWorkflow && <WorkflowStates.Loading />}
          
          {shouldShowError && <WorkflowStates.Error />}
          
          {!isLoadingWorkflow && currentWorkflow && (
            <>
              {/* Same header style as OntologyMigrationPage/ActionEffectMigrationPage: icon + title + description. */}
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    {workflowType === 'process'
                      ? <Workflow className="size-5 text-primary" />
                      : <Sparkles className="size-5 text-primary" />}
                    <h1 className="text-2xl font-semibold tracking-tight">
                      {workflowType === 'process' ? 'Process Migration' : 'Ontology Migration'}
                    </h1>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    {workflowType === 'process'
                      ? 'Migrate published checklists from a source Leucine instance to a target, with their ontology dependencies resolved along the way. Linked processes are detected and handled step by step.'
                      : 'Extract the ontology (ObjectTypes, properties, relations and objects) referenced by the selected checklists and migrate it to the target instance.'}
                  </p>
                </div>
              </div>
              <div>
                {/* Progress bar for the process flow. A "Resolve links" step appears only when
                    the selection turns out to reference linked checklists. */}
                {workflowType === 'process' && (
                  <ProcessStepper
                    className="mb-6"
                    steps={viewState === "linked" || hadLinkedStep
                      ? ["Source", "Target", "Resolve links", "Review", "Done"]
                      : ["Source", "Target", "Review", "Done"]}
                    current={(() => {
                      if (viewState === "linked") {
                        return linkedStage === "resolve" ? 2 : linkedStage === "review" ? 3 : 4;
                      }
                      // With a linked step in this run, Review/Done sit one slot later.
                      const offset = hadLinkedStep ? 1 : 0;
                      switch (viewState) {
                        case "form": return 0;
                        case "scan-review": return 0;
                        case "target-selection": return 1;
                        case "loading":
                        case "results": return 2 + offset;
                        case "migrating": return 3 + offset;
                        case "success": return 4 + offset;
                        default: return 0;
                      }
                    })()}
                  />
                )}

                {/* Single source⇄target instance bar, shared across the source and target
                    steps. Selecting here drives every downstream selector via auth_config. */}
                {(viewState === "form" || viewState === "target-selection") && (
                  <div className="mb-6">
                    {/* Always locked: the pair is chosen on the select-instances step, and the
                        pair capabilities gating the workflow cards were checked for exactly that
                        pair — changing it mid-wizard would bypass the check (and leave stale
                        selections / silently no-op migrations). */}
                    <InstancePicker disabled />
                  </div>
                )}

                {viewState === "form" && (
                  <>
                    {/* Parents deferred in an earlier linked run (waiting for their children to be
                        published on the target) — resume or remove them from here. */}
                    {workflowType === 'process' && (
                      <div className="leu-scope mb-6">
                        <LinkedPipelinePanel hideWhenEmpty />
                      </div>
                    )}
                    <WorkflowDetailsForm />
                    <WorkflowSourceForm onNext={handleSourceFormComplete} />
                  </>
                )}

                {viewState === "scan-review" && (
                  <ChecklistScanGroups
                    entries={linkedScan || []}
                    checklists={selectedChecklists.map((c) => ({ id: c.id, name: c.name, code: c.code }))}
                    onBack={() => setViewState("form")}
                    onContinue={() => setViewState("target-selection")}
                  />
                )}

                {viewState === "target-selection" && (
                  <TargetInstanceForm onNext={handleTargetFormComplete} workflowType={workflowType} />
                )}

                {viewState === "linked" && pairSourceId != null && pairTargetId != null && (
                  <LinkedResolutionFlow
                    sourceServiceId={pairSourceId}
                    sourceFacilityId={selectedFacilityId || ""}
                    sourceUseCaseId={selectedUseCaseId || undefined}
                    checklists={(linkedScan == null ? selectedChecklists : linkedSubset)
                      .map((c) => ({ id: c.id, name: c.name, code: c.code }))}
                    targetServiceId={pairTargetId}
                    targetFacilityId={targetFacilityId || ""}
                    targetUseCaseId={targetUseCaseId || undefined}
                    targetAuthorId={targetAuthorId || undefined}
                    targetChecklistId={targetChecklistId || undefined}
                    description={currentWorkflow?.description || undefined}
                    onNoLinkedChildren={() => continuePlainProcessReview(selectedChecklists)}
                    onBack={() => setViewState("target-selection")}
                    onStartOver={handleReset}
                    onStageChange={setLinkedStage}
                    continueStandard={linkedScan != null && standardSubset.length > 0
                      ? {
                          count: standardSubset.length,
                          onContinue: () => continuePlainProcessReview(standardSubset),
                        }
                      : undefined}
                  />
                )}

                {viewState === "loading" && <WorkflowStates.Processing />}

                {viewState === "results" && (
                  <WorkflowResults
                    onBack={handleBack}
                    onConfirm={handleConfirm}
                    workflowType={workflowType}
                    validationReports={validationReports}
                    validationContextNote={abortContextNote}
                  />
                )}

                {viewState === "migrating" && <WorkflowStates.Migrating />}

                {viewState === "success" && (
                  migrationOutcome
                    ? <MigrationOutcomePanel summary={migrationOutcome} onReset={handleReset} />
                    : <WorkflowStates.Success onReset={handleReset} />
                )}
              </div>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default WorkflowDetailPage;